'use client';

import type React from 'react';
import { Table, TableContainer, TableHead, TableRow } from '@mui/material';

import mixinSx from '@watershed/style/mixinSx';

import usePaletteUtils from '../hooks/usePaletteUtils';

function BasicTableContainer(
  props: React.ComponentProps<typeof TableContainer>
): JSX.Element {
  return (
    <TableContainer
      {...props}
      sx={mixinSx(
        {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
        props.sx
      )}
    />
  );
}

function BasicTable(props: React.ComponentProps<typeof Table>): JSX.Element {
  return (
    <Table
      {...props}
      sx={mixinSx(
        {
          '& th.MuiTableCell-sizeMedium': {
            padding: '16px 12px',
          },
          '& td.MuiTableCell-sizeMedium': {
            padding: '8px 12px',
          },
        },
        props.sx
      )}
    />
  );
}

function BasicTableHead(
  props: React.ComponentProps<typeof TableHead>
): JSX.Element {
  return (
    <TableHead
      {...props}
      sx={mixinSx(
        {
          backgroundColor: (theme) => theme.palette.grey10,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          '& .MuiTableCell-head': {
            fontWeight: 'bold',
            height: 56,
          },
        },
        props.sx
      )}
    />
  );
}

function BasicTableRow({
  className,
  ...props
}: React.ComponentProps<typeof TableRow>): JSX.Element {
  const { boxShadowFocus } = usePaletteUtils();
  return (
    <TableRow
      {...props}
      sx={mixinSx(
        {
          transition: (theme) => theme.transitions.create('background-color'),
          '&[aria-selected]': {
            '& td': {
              cursor: 'pointer',
            },
          },
          '&[aria-selected="false"]:hover': {
            '& td': {
              backgroundColor: (theme) => theme.palette.action.hover,
            },
          },
          '&[aria-selected="true"]': {
            outline: (theme) => `1px solid ${theme.palette.cobalt}`,
            outlineOffset: '-1px', // using outline to simulate a border, offset to allow it to show properly cross browser
            boxShadow: boxShadowFocus,
            '& td': {
              backgroundColor: (theme) => theme.palette.cobalt10,
              borderBottom: 'none',
            },
          },
        },
        props.sx
      )}
    />
  );
}

const BasicTableComponents = {
  BasicTableContainer,
  BasicTable,
  BasicTableHead,
  BasicTableRow,
};

export default BasicTableComponents;
